// Estado do formulário de disparo e as conversões draft ↔ DTO.
//
// O draft é "plano e editável" (strings vazias em vez de undefined, ids de
// UI para as listas); o DTO é o que a API aceita. `validarDraft` espelha o
// DisparoConfiguracaoValidator do backend — a tela não deve deixar montar
// um cadastro que o POST vai recusar.

import {
  AcaoDisparo,
  CanalComunicacao,
  CanalDisparo,
  CatalogoDisparo,
  CombinadorFiltro,
  ComponenteExterno,
  ContextoDisparo,
  CriarDisparoDto,
  DestinatarioDisparo,
  DisparoConfiguracao,
  FormatoVariavel,
  FrequenciaDisparo,
  MAX_CONDICOES_FILTRO,
  MAX_PROFUNDIDADE_FILTRO,
  ModoTemplate,
  OperadorFiltro,
  PLACEHOLDER_COLCHETES_REGEX,
  ProvedorComunicacao,
  TemplateDisparo,
  TipoConteudo,
  TipoGatilho,
  TipoPropriedade,
  VariavelTemplate,
  CHAVE_VARIAVEL_REGEX,
  acaoDoCanal,
  canalAceitaAssunto,
  extrairVariaveis,
  filtroEstaCompleto,
  quantidadeValores,
  variaveisMalformadas,
  rotuloContexto,
} from "@/models/consignado/disparo.model";
import {
  camposDaFrequencia,
  eventosDoContexto,
  limiteCaracteresDoProvedor,
  modosDoProvedor,
  propriedade as buscarPropriedade,
  contatoAutomatico,
  contatoAutomaticoDoContexto,
  rotuloContatoAutomatico,
  provedoresDoCanal,
  remetentesDoProvedor,
} from "./catalogo.helpers";
import {
  MAX_OFFSET_RELATIVO,
  ehDataRelativa,
  instanteDeValorData,
  partesDataRelativa,
} from "./data-relativa";
import { comMoldura, semMoldura } from "./blocoEmail";
import {
  FiltroDraft,
  GrupoNo,
  arvoreDeFiltrosApi,
  condicoesDaArvore,
  gruposVazios,
  novoGrupo,
  paraDtoArvore,
  profundidade,
} from "./filtro-arvore";

// A condição em edição mora no filtro-arvore.ts, junto das operações da
// árvore. Reexportada aqui porque é daqui que a tela sempre a importou.
export { novoFiltro } from "./filtro-arvore";
export type { FiltroDraft };

const novoId = (prefixo: string) => `${prefixo}-${Math.random().toString(36).slice(2, 10)}`;

// ---------------------------------------------------------------------------
// Tipos do draft
// ---------------------------------------------------------------------------

export interface VariavelDraft {
  id: string;
  apelido: string;
  /** Origem do valor: uma propriedade do contexto ou um texto fixo. */
  origem: "PROPRIEDADE" | "CONSTANTE";
  propriedade: string;
  constante: string;
  formato: FormatoVariavel;
  obrigatoria: boolean;
  fallback: string;
}

export interface ParametroDraft {
  id: string;
  componente: ComponenteExterno;
  valor: string;
}

export interface CanalDraft {
  id: string;
  canal: CanalComunicacao;
  provedor: ProvedorComunicacao | null;
  remetente: string;
  origemContato: string;
  fallbacksContato: string[];
  modo: ModoTemplate;
  assunto: string;
  conteudo: string;
  tipoConteudo: TipoConteudo;
  identificador: string;
  idioma: string;
  produtoId: string;
  parametros: ParametroDraft[];
  variaveis: VariavelDraft[];
  /**
   * Template reutilizável de onde o conteúdo veio, quando veio de um.
   * O vínculo é vivo: ao salvar, o backend troca a mensagem do canal pela do
   * template; o detalhe devolve o template atual e o envio também o usa.
   * Com ele, a mensagem é só leitura na tela (TemplateReutilizavelCampo).
   */
  templateId?: string;
}

/**
 * Alteração do draft: um patch direto ou uma função do estado anterior.
 *
 * A forma funcional existe porque uma ação pode escrever duas vezes no mesmo
 * tick — inserir uma variável pela sidebar declara a variável no canal *e*
 * muda o texto dele. Com patch direto, as duas escritas são derivadas do
 * mesmo render e a segunda sobrescreve `canais` inteiro, descartando a
 * primeira: o token entra no texto mas a variável some do mapa, e a tela
 * acusa "sem mapeamento". Quem reescreve uma lista inteira usa a forma
 * funcional.
 */
export type DisparoDraftPatch =
  | Partial<DisparoDraft>
  | ((prev: DisparoDraft) => Partial<DisparoDraft>);

export interface DisparoDraft {
  nome: string;
  ativo: boolean;
  contexto: ContextoDisparo;
  destinatario: DestinatarioDisparo;
  tipoGatilho: TipoGatilho;
  evento: string;
  frequencia: FrequenciaDisparo | null;
  intervalo: number | null;
  hora: string | null;
  diaSemana: number | null;
  diaMes: number | null;
  /** Raiz da árvore de filtros — sempre um grupo, mesmo vazio. */
  filtros: GrupoNo;
  canais: CanalDraft[];
}

// ---------------------------------------------------------------------------
// Construção
// ---------------------------------------------------------------------------

export const novoCanal = (canal: CanalComunicacao): CanalDraft => ({
  id: novoId("canal"),
  canal,
  provedor: null,
  remetente: "",
  origemContato: "",
  fallbacksContato: [],
  modo: ModoTemplate.CORPO_PROPRIO,
  assunto: "",
  conteudo: "",
  tipoConteudo: "TEXTO",
  identificador: "",
  idioma: "",
  produtoId: "",
  parametros: [],
  variaveis: [],
});

/**
 * Rótulo da opção sem template reutilizável em "Origem da mensagem". No
 * WhatsApp não se escreve texto: informa-se o template aprovado no provedor.
 */
export const rotuloMensagemPropria = (modo: ModoTemplate): string =>
  modo === ModoTemplate.REFERENCIA_EXTERNA
    ? "Informar o template do provedor aqui"
    : "Escrever a mensagem aqui";

/** Os modelos da Meta da GranaTech são todos em português do Brasil. */
export const IDIOMA_META = "pt_BR";

export const novoParametro = (componente: ComponenteExterno = "CORPO"): ParametroDraft => ({
  id: novoId("param"),
  componente,
  valor: "",
});

export const novaVariavel = (apelido: string, propriedade: string, formato: FormatoVariavel): VariavelDraft => ({
  id: novoId("var"),
  apelido,
  origem: "PROPRIEDADE",
  propriedade,
  constante: "",
  formato,
  obrigatoria: true,
  fallback: "",
});

export const draftVazio = (): DisparoDraft => ({
  nome: "",
  ativo: false,
  contexto: ContextoDisparo.PROPOSTA,
  destinatario: DestinatarioDisparo.TOMADOR,
  tipoGatilho: TipoGatilho.EVENTO,
  evento: "",
  frequencia: null,
  intervalo: null,
  hora: null,
  diaSemana: null,
  diaMes: null,
  filtros: novoGrupo(CombinadorFiltro.E),
  // Um canal por disparo — ele nasce junto, porque sem canal não há o que
  // cadastrar. O canal em si é trocável no card.
  canais: [novoCanal(CanalComunicacao.EMAIL)],
});

export const variaveisDoTemplate = (template?: TemplateDisparo): VariavelDraft[] =>
  Object.entries(template?.variaveis ?? {}).map(([apelido, v]) => ({
    id: novoId("var"),
    apelido,
    origem: v.constante !== undefined ? "CONSTANTE" : "PROPRIEDADE",
    propriedade: v.propriedade ?? "",
    constante: v.constante ?? "",
    formato: v.formato,
    obrigatoria: v.obrigatoria,
    fallback: v.fallback ?? "",
  }));

const canalDraftDe = (c: CanalDisparo): CanalDraft => ({
  id: novoId("canal"),
  canal: c.canal,
  provedor: c.provedor,
  remetente: c.remetente,
  origemContato: c.origemContato.propriedade,
  fallbacksContato: c.origemContato.fallbacks ?? [],
  modo: c.template?.modo ?? ModoTemplate.CORPO_PROPRIO,
  assunto: c.template?.assunto ?? "",
  conteudo: semMoldura(c.template?.conteudo ?? ""),
  tipoConteudo: c.template?.tipoConteudo ?? "TEXTO",
  identificador: c.template?.referenciaExterna?.identificador ?? "",
  idioma: c.template?.referenciaExterna?.idioma ?? "",
  produtoId: c.template?.referenciaExterna?.produtoId ?? "",
  parametros: (c.template?.referenciaExterna?.parametros ?? []).map((p) => ({
    id: novoId("param"),
    componente: p.componente,
    valor: p.valor,
  })),
  variaveis: variaveisDoTemplate(c.template),
  templateId: c.templateId,
});

export const draftDeDisparo = (d: DisparoConfiguracao): DisparoDraft => ({
  nome: d.nome,
  ativo: d.ativo,
  contexto: d.contexto,
  destinatario: d.destinatario,
  tipoGatilho: d.gatilho.tipo,
  evento: d.gatilho.evento ?? "",
  frequencia: d.gatilho.agendamento?.frequencia ?? null,
  intervalo: d.gatilho.agendamento?.intervalo ?? null,
  hora: d.gatilho.agendamento?.hora ?? null,
  diaSemana: d.gatilho.agendamento?.diaSemana ?? null,
  diaMes: d.gatilho.agendamento?.diaMes ?? null,
  filtros: arvoreDeFiltrosApi(d.filtros),
  canais: d.canais.map(canalDraftDe),
});

// ---------------------------------------------------------------------------
// draft → DTO
// ---------------------------------------------------------------------------

const variavelParaDto = (v: VariavelDraft): VariavelTemplate => {
  const base: VariavelTemplate = { formato: v.formato, obrigatoria: v.obrigatoria };
  if (v.origem === "CONSTANTE") base.constante = v.constante;
  else base.propriedade = v.propriedade;
  if (!v.obrigatoria) base.fallback = v.fallback;
  return base;
};

/**
 * O corpo como vai para o backend: e-mail em HTML ganha a moldura de produção
 * (e perde o parágrafo vazio que o editor deixa no fim). É sobre ele que o
 * backend conta o limite de caracteres do provedor.
 */
export const corpoEnviado = (c: Pick<CanalDraft, "conteudo" | "tipoConteudo">): string =>
  c.tipoConteudo === "HTML" ? comMoldura(c.conteudo) : c.conteudo;

export const templateParaDto = (c: CanalDraft): TemplateDisparo => {
  const variaveis: Record<string, VariavelTemplate> = {};
  c.variaveis.forEach((v) => {
    variaveis[v.apelido] = variavelParaDto(v);
  });

  if (c.modo === ModoTemplate.CORPO_PROPRIO) {
    const template: TemplateDisparo = {
      modo: ModoTemplate.CORPO_PROPRIO,
      conteudo: corpoEnviado(c),
      tipoConteudo: c.tipoConteudo,
      variaveis,
    };
    if (canalAceitaAssunto(c.canal)) template.assunto = c.assunto;
    return template;
  }

  // Índice é por componente, contínuo a partir de zero — o backend recusa
  // lacuna ou repetição, então ele é derivado da ordem, não digitado.
  const contador: Partial<Record<ComponenteExterno, number>> = {};
  const referenciaExterna = {
    identificador: c.identificador,
    parametros: c.parametros.map((p) => {
      const indice = contador[p.componente] ?? 0;
      contador[p.componente] = indice + 1;
      return { componente: p.componente, indice, valor: p.valor };
    }),
  } as TemplateDisparo["referenciaExterna"] & object;

  if (c.provedor === ProvedorComunicacao.META) referenciaExterna.idioma = IDIOMA_META;
  else if (c.idioma) referenciaExterna.idioma = c.idioma;
  if (c.produtoId) referenciaExterna.produtoId = c.produtoId;

  return { modo: ModoTemplate.REFERENCIA_EXTERNA, variaveis, referenciaExterna };
};

// O contato vai calculado (contatoAutomatico): o backend novo o ignora e
// decide sozinho; o anterior ainda exige o campo.
const canalParaDto = (c: CanalDraft, destinatario: DestinatarioDisparo): CanalDisparo => ({
  acao: acaoDoCanal(c.canal) as AcaoDisparo,
  canal: c.canal,
  provedor: c.provedor as ProvedorComunicacao,
  remetente: c.remetente,
  origemContato: { propriedade: contatoAutomatico(destinatario, c.canal) },
  ...(c.templateId ? { templateId: c.templateId } : {}),
  template: templateParaDto(c),
});

/**
 * Só a rotina filtra (decisão do tech lead): o disparo por evento vale para o
 * registro do evento. A seção de filtro some da tela e o evento vai sem filtro.
 */
export const gatilhoUsaFiltro = (tipo: TipoGatilho): boolean => tipo !== TipoGatilho.EVENTO;

export const draftParaDto = (d: DisparoDraft): CriarDisparoDto => {
  const dto: CriarDisparoDto = {
    nome: d.nome.trim(),
    ativo: d.ativo,
    contexto: d.contexto,
    destinatario: d.destinatario,
    gatilho: { tipo: d.tipoGatilho },
    // Evento vai sem filtro mesmo que o rascunho guarde um (de antes da troca
    // de gatilho, ou gravado quando o evento ainda filtrava).
    filtros: paraDtoArvore(
      gatilhoUsaFiltro(d.tipoGatilho) ? d.filtros : novoGrupo(CombinadorFiltro.E),
    ),
    canais: d.canais.map((c) => canalParaDto(c, d.destinatario)),
  };

  if (d.tipoGatilho === TipoGatilho.EVENTO) {
    dto.gatilho.evento = d.evento;
  } else if (d.tipoGatilho === TipoGatilho.ROTINA && d.frequencia) {
    // Só os campos que a frequência declara — qualquer extra é recusado
    // ("Campo X incompatível com Y"), então o que está nulo fica de fora.
    dto.gatilho.agendamento = {
      frequencia: d.frequencia,
      timezone: "America/Sao_Paulo",
      ...(d.intervalo != null ? { intervalo: d.intervalo } : {}),
      ...(d.hora ? { hora: d.hora } : {}),
      ...(d.diaSemana != null ? { diaSemana: d.diaSemana } : {}),
      ...(d.diaMes != null ? { diaMes: d.diaMes } : {}),
    };
  }

  return dto;
};

// ---------------------------------------------------------------------------
// Validação — espelha DisparoConfiguracaoValidator
// ---------------------------------------------------------------------------

/** Erros do cadastro: chaves do cabeçalho, mais um mapa por canal e por filtro. */
export interface DisparoErros {
  nome?: string;
  evento?: string;
  frequencia?: string;
  intervalo?: string;
  hora?: string;
  diaSemana?: string;
  diaMes?: string;
  filtros?: string;
  canais?: string;
  /** Por id de filtro. */
  porFiltro: Record<string, string>;
  /** Por id de canal. */
  porCanal: Record<string, string>;
}

export const semErros = (erros: DisparoErros): boolean =>
  !erros.nome &&
  !erros.evento &&
  !erros.frequencia &&
  !erros.intervalo &&
  !erros.hora &&
  !erros.diaSemana &&
  !erros.diaMes &&
  !erros.filtros &&
  !erros.canais &&
  Object.keys(erros.porFiltro).length === 0 &&
  Object.keys(erros.porCanal).length === 0;

const validarFiltro = (
  filtro: FiltroDraft,
  draft: DisparoDraft,
  catalogo?: CatalogoDisparo,
): string | null => {
  if (!filtro.propriedade) return "Escolha o campo.";
  const p = buscarPropriedade(catalogo, draft.contexto, filtro.propriedade);
  if (!p) return "Campo não disponível neste contexto.";
  if (!p.operadores.includes(filtro.operador)) return "Operador não permitido para este campo.";

  const preenchidos = filtro.valores.filter((v) => v.trim() !== "");
  const esperado = quantidadeValores(filtro.operador);
  if (esperado === null) {
    if (preenchidos.length === 0) return "Informe ao menos um valor.";
    if (preenchidos.length > 30) return "No máximo 30 valores.";
  } else if (preenchidos.length !== esperado) {
    return esperado === 2 ? "Informe início e fim." : "Informe o valor.";
  }

  if (p.valores && preenchidos.some((v) => !p.valores?.includes(v))) {
    return "Valor fora dos permitidos para este campo.";
  }
  // Data aceita o valor fixo e o token relativo (`AGORA-7d`). Os dois passam
  // pela mesma resolução, e o teto do deslocamento é o do backend.
  if (p.tipo === TipoPropriedade.DATA) {
    if (preenchidos.some((v) => instanteDeValorData(v) === null)) {
      return "Data inválida.";
    }
    const excedido = preenchidos.some(
      (v) => ehDataRelativa(v) && (partesDataRelativa(v)?.quantidade ?? 0) > MAX_OFFSET_RELATIVO,
    );
    if (excedido) return `O deslocamento máximo é ${MAX_OFFSET_RELATIVO}.`;
  }
  if (p.tipo === TipoPropriedade.NUMERO && preenchidos.some((v) => !Number.isFinite(Number(v)))) {
    return "Número inválido.";
  }
  if (filtro.operador === OperadorFiltro.ENTRE && preenchidos.length === 2) {
    // Comparar como texto funcionava enquanto toda data era YYYY-MM-DD. Com
    // `AGORA-7d` no meio, "AGORA-7d" > "AGORA" na ordem alfabética e o período
    // mais comum de todos seria recusado — então as duas pontas viram instante
    // antes de comparar.
    if (p.tipo === TipoPropriedade.DATA) {
      const inicio = instanteDeValorData(preenchidos[0]);
      const fim = instanteDeValorData(preenchidos[1]);
      if (inicio === null || fim === null) return "Data inválida.";
      if (inicio >= fim) return "O início deve ser anterior ao fim.";
    } else if (p.tipo === TipoPropriedade.NUMERO) {
      if (Number(preenchidos[0]) >= Number(preenchidos[1])) {
        return "O valor inicial deve ser menor que o final.";
      }
    } else if (preenchidos[0] >= preenchidos[1]) {
      return "O início deve ser anterior ao fim.";
    }
  }
  return null;
};

const textosDoCanal = (c: CanalDraft): string[] =>
  c.modo === ModoTemplate.CORPO_PROPRIO
    ? [c.conteudo, c.assunto]
    : c.parametros.map((p) => p.valor);

/**
 * Descarta as variáveis cujo apelido saiu do texto.
 *
 * O mapa de variáveis deixou de ser editável: ele nasce ao escolher um campo
 * na barra lateral e não tem mais tabela com botão de remover. Sem esta
 * limpeza, apagar `{{apelido}}` do texto deixaria a variável órfã no draft, e
 * a validação cobraria ("não é usada na mensagem") um erro que a tela não dá
 * como corrigir.
 */
/**
 * Mensagem sem variáveis, para a troca de contexto: as propriedades eram do
 * contexto anterior. Sai a declaração e sai o `{{apelido}}` do texto — senão o
 * token ficaria sem mapeamento. Com template vinculado, o vínculo também sai:
 * o template pertence ao contexto anterior.
 */
export const semVariaveis = (c: CanalDraft): CanalDraft => {
  if (!c.variaveis.length && !c.templateId) return c;
  const tokens = c.variaveis.map(
    (v) => new RegExp(`\\{\\{\\s*${v.apelido}\\s*\\}\\} ?`, "g"),
  );
  const tirar = (texto: string) => tokens.reduce((t, re) => t.replace(re, ""), texto);
  return {
    ...c,
    templateId: undefined,
    conteudo: tirar(c.conteudo),
    assunto: tirar(c.assunto),
    parametros: c.parametros.map((p) => ({ ...p, valor: tirar(p.valor) })),
    variaveis: [],
  };
};

export const limparVariaveisOrfas = (c: CanalDraft): CanalDraft => {
  const usadas = new Set<string>();
  textosDoCanal(c).forEach((t) => extrairVariaveis(t).forEach((v) => usadas.add(v)));
  const restantes = c.variaveis.filter((v) => usadas.has(v.apelido));
  return restantes.length === c.variaveis.length ? c : { ...c, variaveis: restantes };
};

const validarVariaveis = (
  c: CanalDraft,
  contexto: ContextoDisparo,
  catalogo?: CatalogoDisparo,
): string | null => {
  const textos = textosDoCanal(c);
  if (textos.some((t) => PLACEHOLDER_COLCHETES_REGEX.test(t))) {
    return "Troque os placeholders entre colchetes por {{variavel}}.";
  }
  const malformada = textos.flatMap(variaveisMalformadas)[0];
  if (malformada) {
    return `Variável mal escrita: ${malformada}. Use {{nome}} com letras sem acento, números ou _, começando por letra.`;
  }

  const usadas = new Set<string>();
  textos.forEach((t) => extrairVariaveis(t).forEach((v) => usadas.add(v)));

  if (c.variaveis.length > 50) return "No máximo 50 variáveis por mensagem.";

  const apelidos = c.variaveis.map((v) => v.apelido);
  if (new Set(apelidos).size !== apelidos.length) return "Há variáveis com o mesmo nome.";

  for (const nome of usadas) {
    if (!apelidos.includes(nome)) return `A mensagem usa {{${nome}}}, que não está mapeada.`;
  }

  for (const v of c.variaveis) {
    if (!CHAVE_VARIAVEL_REGEX.test(v.apelido)) {
      return `Nome de variável inválido: ${v.apelido || "(vazio)"}.`;
    }
    if (!usadas.has(v.apelido)) return `A variável ${v.apelido} não é usada na mensagem.`;
    if (!v.obrigatoria && !v.fallback.trim()) {
      return `A variável ${v.apelido} não exige valor — informe o texto padrão que entra quando o campo vier vazio.`;
    }
    if ([v.constante, v.fallback].some((s) => s && PLACEHOLDER_COLCHETES_REGEX.test(s))) {
      return "Valor fixo e valor padrão não podem conter placeholders.";
    }
    if (v.origem === "CONSTANTE") {
      if (v.formato !== FormatoVariavel.TEXTO) return `A variável ${v.apelido} é texto fixo — use o formato Texto.`;
      if (v.obrigatoria && !v.constante.trim()) return `Informe o texto fixo da variável ${v.apelido}.`;
    } else {
      if (!v.propriedade) return `Escolha o campo da variável ${v.apelido}.`;
      const p = buscarPropriedade(catalogo, contexto, v.propriedade);
      if (!p) return `Campo da variável ${v.apelido} não disponível neste contexto.`;
      if (!p.formatos.includes(v.formato)) {
        return `O formato escolhido não serve para o campo da variável ${v.apelido}.`;
      }
    }
  }
  return null;
};

/**
 * A mensagem em si: provedor, modo, corpo (ou referência externa) e o mapa
 * de variáveis. Vale tanto para o canal de um disparo quanto para um
 * template reutilizável — nos dois casos é o mesmo TemplateDisparo.
 *
 * O POST /disparos/templates só roda a validação de DTO, então um template
 * mal montado passa ali e só estoura quando alguém o usa num disparo (é o
 * DisparoConfiguracaoValidator que carrega o conteúdo salvo e o valida).
 * Cobrar aqui evita gravar um template que já nasce quebrado.
 */
export const validarMensagem = (
  c: CanalDraft,
  contexto: ContextoDisparo,
  catalogo?: CatalogoDisparo,
): string | null => {
  if (!c.provedor) return "Selecione o provedor.";
  if (!provedoresDoCanal(catalogo, c.canal).includes(c.provedor)) {
    return "Este provedor não atende o canal.";
  }
  if (!modosDoProvedor(catalogo, c.canal, c.provedor).includes(c.modo)) {
    return "Este provedor não aceita esse tipo de mensagem.";
  }

  if (c.modo === ModoTemplate.CORPO_PROPRIO) {
    if (!c.conteudo.trim()) return "Escreva a mensagem.";
    if (canalAceitaAssunto(c.canal) && !c.assunto.trim()) return "E-mail exige assunto.";
    if (!canalAceitaAssunto(c.canal) && c.tipoConteudo === "HTML") {
      return "HTML só vale para e-mail.";
    }
    const limite = limiteCaracteresDoProvedor(catalogo, c.canal, c.provedor);
    const tamanho = corpoEnviado(c).length;
    if (limite !== undefined && tamanho > limite) {
      const comHtml = c.tipoConteudo === "HTML" ? ", contando as tags e estilos do HTML," : "";
      return `A mensagem tem ${tamanho} caracteres${comHtml} e o limite deste canal é ${limite}.`;
    }
  } else {
    if (!c.identificador.trim()) return "Informe o identificador do template no provedor.";
    if (c.provedor === ProvedorComunicacao.META) {
      if (c.produtoId.trim()) return "A Meta não usa ID de produto.";
    }
    if (c.provedor === ProvedorComunicacao.VONEX) {
      if (!c.produtoId.trim()) return "A Vonex exige o ID do produto.";
      if (!/^[1-9]\d*$/.test(c.identificador.trim())) {
        return "Na Vonex o identificador do template é numérico.";
      }
      if (c.idioma.trim()) return "A Vonex não usa idioma.";
      if (c.parametros.some((p) => p.componente !== "CORPO")) {
        return "A Vonex só aceita parâmetros de corpo.";
      }
    }
    for (const componente of ["BOTAO_URL", "CABECALHO_IMAGEM"] as ComponenteExterno[]) {
      if (c.parametros.filter((p) => p.componente === componente).length > 1) {
        return "No máximo um parâmetro por botão ou cabeçalho.";
      }
    }
    if (c.parametros.some((p) => !p.valor.trim())) return "Preencha os parâmetros do template.";
  }

  return validarVariaveis(c, contexto, catalogo);
};

/** Contexto sem o contato que o backend vai usar: o disparo não teria para onde enviar. */
const semContatoAutomatico = (
  c: CanalDraft,
  draft: DisparoDraft,
  catalogo?: CatalogoDisparo,
): string | null =>
  !catalogo || contatoAutomaticoDoContexto(catalogo, draft.contexto, draft.destinatario, c.canal)
    ? null
    : `Este contexto não tem o ${rotuloContatoAutomatico(draft.destinatario, c.canal)} — o disparo não teria para onde enviar.`;

/** Um canal do disparo: a mensagem, mais o remetente e o contato automático. */
const validarCanal = (
  c: CanalDraft,
  draft: DisparoDraft,
  catalogo?: CatalogoDisparo,
): string | null => {
  const erroMensagem = validarMensagem(c, draft.contexto, catalogo);
  if (erroMensagem) return erroMensagem;

  if (!c.remetente) return "Selecione o remetente.";
  if (c.provedor && !remetentesDoProvedor(catalogo, c.canal, c.provedor).includes(c.remetente)) {
    return "Remetente não aceito por este provedor.";
  }

  const erroContato = semContatoAutomatico(c, draft, catalogo);
  if (erroContato) return erroContato;

  return null;
};

export const validarDraft = (draft: DisparoDraft, catalogo?: CatalogoDisparo): DisparoErros => {
  const erros: DisparoErros = { porFiltro: {}, porCanal: {} };
  // Os tetos do backend valem para a árvore inteira, não por grupo. Evento
  // não manda filtro, então a árvore guardada no rascunho não é validada.
  const usaFiltro = gatilhoUsaFiltro(draft.tipoGatilho);
  const condicoes = usaFiltro ? condicoesDaArvore(draft.filtros) : [];

  if (!draft.nome.trim()) erros.nome = "Informe um nome.";

  if (draft.tipoGatilho === TipoGatilho.EVENTO) {
    if (!draft.evento) {
      // Contexto sem nenhum evento (hoje, Cobrança CLT): "Selecione o evento."
      // mandava escolher numa lista vazia.
      erros.evento =
        catalogo && eventosDoContexto(catalogo, draft.contexto).length === 0
          ? semEventoNoContexto(draft.contexto, catalogo)
          : "Selecione o evento.";
    } else {
      erros.evento = erroDeEventoDoContexto(draft, catalogo);
      const ev = catalogo?.eventos.find((e) => e.id === draft.evento);
      if (!erros.evento && draft.ativo && ev?.definicaoPendente) {
        erros.evento = "Evento pendente de definição — só pode ser salvo inativo.";
      }
    }
  } else {
    if (condicoes.length === 0) {
      erros.filtros =
        draft.tipoGatilho === TipoGatilho.ROTINA
          ? "Rotina exige ao menos um filtro."
          : "Manual exige ao menos um filtro.";
    }
    if (draft.tipoGatilho === TipoGatilho.ROTINA) {
      if (!draft.frequencia) {
        erros.frequencia = "Selecione a frequência.";
      } else {
        const campos = camposDaFrequencia(catalogo, draft.frequencia);
        const spec = catalogo?.agendamentos.frequencias.find((f) => f.id === draft.frequencia);
        if (campos.includes("intervalo")) {
          if (!draft.intervalo) erros.intervalo = "Selecione o intervalo.";
          else if (spec?.intervalos && !spec.intervalos.includes(draft.intervalo)) {
            erros.intervalo = "Intervalo não suportado.";
          }
        }
        if (campos.includes("hora") && !draft.hora) erros.hora = "Informe o horário.";
        if (campos.includes("diaSemana") && draft.diaSemana == null) {
          erros.diaSemana = "Selecione o dia da semana.";
        }
        if (campos.includes("diaMes") && draft.diaMes == null) {
          erros.diaMes = "Selecione o dia do mês.";
        }
      }
    }
  }

  if (!usaFiltro) {
    // nada a validar na árvore
  } else if (condicoes.length > MAX_CONDICOES_FILTRO) {
    erros.filtros = `No máximo ${MAX_CONDICOES_FILTRO} filtros.`;
  } else if (profundidade(draft.filtros) > MAX_PROFUNDIDADE_FILTRO) {
    // A tela já desabilita o botão de subgrupo no teto; isto cobre uma
    // árvore que chegou pronta da API mais funda do que o cadastro monta.
    erros.filtros = "Grupos aninhados demais — desfaça um dos grupos.";
  } else if (gruposVazios(draft.filtros).length > 0) {
    // Grupo vazio não é inofensivo: num OU ele vale FALSE e zera a busca.
    erros.filtros = "Há grupo sem condição — adicione uma condição ou remova o grupo.";
  }
  condicoes.forEach((f) => {
    const erro = validarFiltro(f, draft, catalogo);
    if (erro) erros.porFiltro[f.id] = erro;
  });

  // O cadastro é de canal único, então a lista tem exatamente um item. O
  // backend aceita de 1 a 3 — mandar 1 é sempre válido.
  if (draft.canais.length !== 1) {
    erros.canais = "O disparo precisa de exatamente um canal.";
  }
  draft.canais.forEach((c) => {
    const erro = validarCanal(c, draft, catalogo);
    if (erro) erros.porCanal[c.id] = erro;
  });

  return erros;
};

/** Texto para o gatilho por evento num contexto que nenhum evento aciona. */
export const semEventoNoContexto = (contexto: ContextoDisparo, catalogo?: CatalogoDisparo): string =>
  `Nenhum evento aciona disparos de ${rotuloContexto(contexto, catalogo)}. Use o gatilho Rotina.`;

/** Evento escolhido que não vale para o contexto (ou saiu do catálogo). */
const erroDeEventoDoContexto = (
  draft: DisparoDraft,
  catalogo?: CatalogoDisparo,
): string | undefined => {
  if (draft.tipoGatilho !== TipoGatilho.EVENTO || !draft.evento) return undefined;
  const ev = catalogo?.eventos.find((e) => e.id === draft.evento);
  return !ev || !ev.contextos.includes(draft.contexto)
    ? "Evento incompatível com o contexto escolhido."
    : undefined;
};

/**
 * Só o que a troca de contexto invalidou no que o usuário já preencheu:
 * evento, campo de filtro, variável ou contato que não existe no contexto
 * novo. Campo ainda vazio não entra — cobrar "Selecione o evento." ou "Escolha
 * o campo." num cadastro pela metade, só porque o contexto mudou, é acusar o
 * que o usuário nem chegou a preencher. O resto fica para o Salvar.
 *
 * `evento` volta sempre, mesmo vazio: a troca de volta para um contexto
 * compatível precisa apagar o erro que a troca anterior acendeu.
 */
export const validarContexto = (
  draft: DisparoDraft,
  catalogo?: CatalogoDisparo,
): Pick<DisparoErros, "porFiltro" | "porCanal" | "evento"> => {
  const erros: Pick<DisparoErros, "porFiltro" | "porCanal" | "evento"> = {
    porFiltro: {},
    porCanal: {},
    evento: erroDeEventoDoContexto(draft, catalogo),
  };

  (gatilhoUsaFiltro(draft.tipoGatilho) ? condicoesDaArvore(draft.filtros) : []).forEach((f) => {
    if (!f.propriedade) return;
    // Condição completa passa pela validação inteira (operador e valores
    // também dependem do campo); pela metade, só se o campo sumiu.
    const erro = filtroEstaCompleto(f)
      ? validarFiltro(f, draft, catalogo)
      : buscarPropriedade(catalogo, draft.contexto, f.propriedade)
        ? null
        : "Campo não disponível neste contexto.";
    if (erro) erros.porFiltro[f.id] = erro;
  });

  draft.canais.forEach((c) => {
    const variavel = c.variaveis.find(
      (v) =>
        v.origem === "PROPRIEDADE" &&
        v.propriedade &&
        !buscarPropriedade(catalogo, draft.contexto, v.propriedade),
    );
    if (variavel) {
      erros.porCanal[c.id] = `Campo da variável ${variavel.apelido} não disponível neste contexto.`;
      return;
    }
    const erroContato = semContatoAutomatico(c, draft, catalogo);
    if (erroContato) erros.porCanal[c.id] = erroContato;
  });

  return erros;
};
